import type { Player } from '@/types/Player.types'
import type { ControlPoint, Coordinate, Drawing } from '@/types'
import { FIELD_WIDTH_FEET } from '@/constants'

/**
 * Flip an X coordinate horizontally around the field center
 */
function flipX(x: number): number {
	return FIELD_WIDTH_FEET - x
}

/**
 * Flip a coordinate horizontally
 */
function flipCoordinate(coord: Coordinate): Coordinate {
	return { x: flipX(coord.x), y: coord.y }
}

/**
 * Flip a control point horizontally (including bezier handles)
 */
function flipControlPoint(point: ControlPoint): ControlPoint {
	return {
		...point,
		x: flipX(point.x),
		handleIn: point.handleIn ? flipCoordinate(point.handleIn) : undefined,
		handleOut: point.handleOut ? flipCoordinate(point.handleOut) : undefined,
	}
}

/**
 * Swap left/right labels for linemen positions
 */
function flipLabel(label: string | undefined): string | undefined {
	if (!label) return label
	const labelUpper = label.toUpperCase()
	// Swap left/right for linemen
	if (labelUpper === 'LT') return 'RT'
	if (labelUpper === 'RT') return 'LT'
	if (labelUpper === 'LG') return 'RG'
	if (labelUpper === 'RG') return 'LG'
	// Center and other labels stay the same
	return label
}

/**
 * Flip a player horizontally
 */
export function flipPlayer(player: Player): Player {
	return {
		...player,
		x: flipX(player.x),
		label: flipLabel(player.label) ?? player.label,
	}
}

/**
 * Flip a drawing horizontally
 */
export function flipDrawing(drawing: Drawing): Drawing {
	const flippedPoints: Record<string, ControlPoint> = {}
	for (const [id, point] of Object.entries(drawing.points)) {
		flippedPoints[id] = flipControlPoint(point)
	}
	return {
		...drawing,
		points: flippedPoints,
	}
}

/**
 * Flip all players and drawings horizontally
 */
export function flipCanvasHorizontally(
	players: Player[],
	drawings: Drawing[],
): { players: Player[]; drawings: Drawing[] } {
	return {
		players: players.map(flipPlayer),
		drawings: drawings.map(flipDrawing),
	}
}

/**
 * Swap hash mark from left to right and vice versa.
 * Middle and null stay the same.
 */
function swapHashMark(hashMark: 'left' | 'middle' | 'right' | null | undefined): 'left' | 'middle' | 'right' | null | undefined {
	if (hashMark === 'left') return 'right'
	if (hashMark === 'right') return 'left'
	return hashMark // middle or null stays the same
}

/**
 * Position input type for formation player positions (without DB-specific fields)
 */
type FormationPositionInput = {
	role: string
	position_x: number
	position_y: number
	hash_relative?: boolean
	hash_mark?: 'left' | 'middle' | 'right' | null
	metadata?: { color?: string; isLineman?: boolean }
}

/**
 * Swap left/right roles for linemen positions
 */
function flipRole(role: string): string {
	const roleUpper = role.toUpperCase()
	// Swap left/right for linemen
	if (roleUpper === 'LT') return 'RT'
	if (roleUpper === 'RT') return 'LT'
	if (roleUpper === 'LG') return 'RG'
	if (roleUpper === 'RG') return 'LG'
	// Center and other roles stay the same
	return role
}

/**
 * Mirror formation positions horizontally.
 * Flips X coordinates, swaps left/right hash marks, and swaps lineman roles.
 * Used for auto-mirror formations to create mirrored pairs.
 */
export function mirrorFormationPositions(positions: FormationPositionInput[]): FormationPositionInput[] {
	return positions.map(pos => ({
		...pos,
		role: flipRole(pos.role),
		position_x: FIELD_WIDTH_FEET - pos.position_x,
		hash_mark: swapHashMark(pos.hash_mark),
	}))
}
